import Dispatcher from "Dispatcher";
import PropTypes from "prop-types";
import { ReduceStore } from "flux/utils";
import typeCheckingStore from "data/util/typeCheckingStore";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import MergeActions from "./MergeActions";
import MappingActions from "./MappingActions";
import SuperRecordActions from "./SuperRecordActions";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import SuperRecordStore from "data/SuperRecordStore";
import TableStore from "data/TableStore";
import NavigatorStore from "data/NavigatorStore";
import MappingStore from "data/MappingStore";
import OverStore from "data/OverStore";
import invariant from "invariant";
import { isConfirmed } from "./OverStore";
import lockStore from "./lockStore";
import { recordIdType } from "data/util/typedefs";
import SearchFormStore from "./SearchFormStore";

export const MERGE_PLUGINS = {
    GLOBAL_MERGE: "global-merge",
    RECORD_MERGE: "record-merge",
    CHILD_MERGE: "child-merge"
};

export const MERGE_CONTEXT = ":merge";
export const isMergeContext = (contextKey) =>
    (contextKey || "").indexOf(MERGE_CONTEXT) !== -1;

const isReady = state => !state.active; // not currently merging anything

const processLaunchDialogState = (state, action) => {
    const fluxContext = action[ACTION_CONTEXT_KEY];
    invariant(fluxContext, "merge must be initiated w/ a contexted dispatch");
    if(!Object.values(MERGE_PLUGINS).includes(action.plugin)) {
        throw Error(`Unknown plugin provided "${action.plugin}"`);
    }
    return {
        ...state,
        active: false,
        interactive: true,
        modalParams: {
            ...state.modalParams,
            drawerOpen: true,
            title: action.title,
        },
        pluginKey: action.plugin,
        target: action.target,
        parent: action.parent || null,
        source: action.source || null,
    };
};

const processSelectSourceState = (state, action) => ({
        ...state,
        source: action.source,
        modalParams: {
            ...state.modalParams,
            drawerOpen: false,
        },
    });

const processToggleDrawerState = (state) => ({
        ...state,
        modalParams: {
            ...state.modalParams,
            drawerOpen: !state.modalParams.drawerOpen,
        },
    });

const processDoMergeState = (state, action) => {
    if(!isConfirmed(action)) return state;
    const fluxContext = action[ACTION_CONTEXT_KEY];
    invariant(fluxContext, "merge must be initiated w/ a contexted dispatch");
    invariant(state.interactive, "global merge must be interactive.");
    invariant(state.source.id && (state.source.stereoKey || state.source.entityKey),
        "global source must be StereotypeProxy or EntityProxy.");
    invariant(state.target.id && (state.target.stereoKey || state.target.entityKey),
        "global target must be StereotypeProxy or EntityProxy.");
    return {
        ...state,
        active: true,
        parent: action.parent || state.parent || null,
        pluginKey: action.plugin || state.pluginKey,
    };
};

class MergeStore extends ReduceStore {
    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {
            plugin: null,
            active: false,
            type: null,
            parent: null,
            target: null,
            source: null,
            modalParams: {
                title: null,
                drawerOpen: true,
            },
            interactive: false,
        };
    }

    reduce(state, action) {
        switch (action.type) {
            case MergeActions.LAUNCH_DIALOG: {
                this.__dispatcher.waitFor([
                    NavigatorStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                    TableStore.getDispatchToken(),
                ]);
                return processLaunchDialogState(this.getInitialState(), action);
            }

            case MergeActions.SELECT_SOURCE: {
                return processSelectSourceState(state, action);
            }

            case MergeActions.TOGGLE_DRAWER: {
                return processToggleDrawerState(state);
            }

            case MergeActions.DO_MERGE: {
                return processDoMergeState(state, action);
            }

            case MappingActions.IT_IS_DONE: {
                if(!state.active) return state;
                this.__dispatcher.waitFor([
                    NavigatorStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                    TableStore.getDispatchToken(),
                    MappingStore.getDispatchToken(),
                    SearchFormStore.getDispatchToken(),
                ]);
                if(state.pluginKey === MERGE_PLUGINS.GLOBAL_MERGE) {
                    // global merge requires a save to happen.
                    // Save gets invoked in SuperRecordStore.
                    // Global merge is not complete until
                    // SuperRecordActions.UPDATED is dispatched...
                    return state;
                }
                // wait for other stores
                return this.getInitialState(); // reset merge state... all done!
            }

            case SuperRecordActions.UPDATED: {
                if(!state.active) return state;
                if(state.pluginKey !== MERGE_PLUGINS.GLOBAL_MERGE) return state;
                this.__dispatcher.waitFor([
                    NavigatorStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                    TableStore.getDispatchToken(),
                    OverStore.getDispatchToken(),
                    lockStore.getDispatchToken(),
                    SearchFormStore.getDispatchToken(),
                ]);
                // GLOBAL MERGE SUCCESSFUL!!!
                return this.getInitialState(); // reset merge state... all done!

            }

            case MergeActions.CANCEL:
            case MappingActions.CANCEL:
            case SuperRecordActions.CANCEL_FOR_OPTLOCK: {
                if(!state.interactive) return state;
                return this.getInitialState(); // reset merge state... all done!
            }

            case MergeActions.CANCEL_FOR_SOURCE_OPTLOCK: {
                this.__dispatcher.waitFor([
                    lockStore.getDispatchToken(),
                ]);
                return {
                    ...state,
                    source: null,
                    modalParams: {
                        ...state.modalParams,
                        drawerOpen: true,
                    },
                };
            }

            case SuperRecordActions.UPDATE_ERROR: {
                if(!state.active) return state;
                if(state.pluginKey !== MERGE_PLUGINS.GLOBAL_MERGE) return state;
                // error happened during global merge
                // reset the active state so they can try again.
                return {...state, active: false};
            }

            default:
                return state;
        }
    }

    isReady() {
        return isReady(this.getState());
    }

    getPluginKey() {
        return this.getState().pluginKey;
    }

    getProxyPair() {
        const {source: src, target: tgt} = this.getState();
        return {
            source: TableStore.getStereotypeProxy(src.stereoKey, src.id),
            target: TableStore.getStereotypeProxy(tgt.stereoKey, tgt.id),
        };
    }

    getDeleteAction() {
        // builds the action used for deleting the source record.
        const state = this.getState();
        switch(state.pluginKey) {
            case MERGE_PLUGINS.RECORD_MERGE: {
                return {
                    recordKey: state.target.entityKey,
                    recordId: state.source.id, // source id
                    targetId: state.target.id, // target id
                };
            }
            case MERGE_PLUGINS.CHILD_MERGE: {
                const ppInfo = entityFieldMetaStore.getInfo(state.target.entityInfo.key, state.target.entityInfo.parentPointerKey);
                return {
                    recordKey: state.parent.entityKey, // parent key
                    recordId: state.parent.id, // parent id
                    fieldKey: ppInfo.inverseKey, // source field
                    childKey: state.source.entityKey, // source key
                    childId: state.source.id, // source id
                    targetId: state.target.id, //target id
                };
            }
            default:
                throw Error(`Unknown merge plugin key "${state.pluginKey}"`);
        }
    }
}

export const epIshShape = PropTypes.exact({
    entityKey: PropTypes.string.isRequired,
    id: recordIdType.isRequired,
});

export const spIshShape = PropTypes.exact({
    stereoKey: PropTypes.string.isRequired,
    id: recordIdType.isRequired,
});

MergeStore.stateTypes = {
    active: PropTypes.bool.isRequired,
    pluginKey: PropTypes.string,
    parent: epIshShape,
    target: PropTypes.oneOfType([epIshShape, spIshShape]),
    source: PropTypes.oneOfType([epIshShape, spIshShape]),
};

export default typeCheckingStore(new MergeStore());



// WEBPACK FOOTER //
// ./app/data/MergeStore.js